'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Box, Button, Chip, FormControlLabel, Switch, TextField } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import {
  useSaveTomVendingCategory,
  useTomVendingCategoriesList,
  type TvCategory,
} from '@/hooks/useTomVendingInventory';
import { useNotification } from '@/contexts/NotificationContext';

const categoryFieldGroups = [
  {
    groupName: 'Category',
    fields: [
      { label: 'Name', name: 'name', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
      { label: 'Active', name: 'active', type: GENERIC_FORM_FIELD_TYPE.SWITCH, required: false, columns: 12 },
    ],
  },
];

const TomsVendingCategoriesPage: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const saveMutation = useSaveTomVendingCategory();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TvCategory | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState([] as { id: string; desc: boolean }[]);

  const { data: list = [], isLoading, refetch } = useTomVendingCategoriesList(search, showInactive);

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((c) => (c.name || '').toLowerCase().includes(term));
  }, [list, search]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: TvCategory) => {
    setEditing(row);
    setModalOpen(true);
  }, []);

  const handleDeactivate = useCallback(
    async (row: TvCategory) => {
      if (!window.confirm(`Deactivate category "${row.name}"?`)) return;
      try {
        await saveMutation.mutateAsync({ id: row.id, name: row.name, active: false });
        showSuccess('Category deactivated');
        await refetch();
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to deactivate');
      }
    },
    [refetch, saveMutation, showError, showSuccess],
  );

  const handleReactivate = useCallback(
    async (row: TvCategory) => {
      try {
        await saveMutation.mutateAsync({ id: row.id, name: row.name, active: true });
        showSuccess('Category reactivated');
        await refetch();
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to reactivate');
      }
    },
    [refetch, saveMutation, showError, showSuccess],
  );

  const columns = useMemo<MRT_ColumnDef<TvCategory>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', size: 70 },
      { accessorKey: 'name', header: 'Name', size: 240 },
      {
        accessorKey: 'active',
        header: 'Status',
        size: 100,
        Cell: ({ row }) => (
          <Chip
            label={row.original.active === false ? 'Inactive' : 'Active'}
            size="small"
            color={row.original.active === false ? 'default' : 'success'}
            variant="outlined"
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 80,
        Cell: ({ row }) => {
          const c = row.original;
          const isActive = c.active !== false;
          return (
            <GenericActionPicker
              row={c}
              actions={[
                {
                  icon: GENERIC_ICON_NAME.EDIT,
                  label: 'Edit',
                  onClick: (r) => openEdit(r),
                },
                ...(isActive
                  ? [
                      {
                        icon: GENERIC_ICON_NAME.BLOCK,
                        label: 'Deactivate',
                        onClick: (r: TvCategory) => void handleDeactivate(r),
                      },
                    ]
                  : [
                      {
                        icon: GENERIC_ICON_NAME.CHECK_CIRCLE,
                        label: 'Reactivate',
                        onClick: (r: TvCategory) => void handleReactivate(r),
                      },
                    ]),
              ]}
            />
          );
        },
      },
    ],
    [handleDeactivate, handleReactivate, openEdit],
  );

  const modalInitialValues = editing
    ? { name: editing.name ?? '', active: editing.active !== false }
    : { name: '', active: true };

  return (
    <>
      <GenericFormModal
        visible={modalOpen}
        onDismiss={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={async (values: Record<string, unknown>) => {
          const name = String(values.name ?? '').trim();
          if (!name) {
            showError('Name is required');
            return;
          }
          try {
            await saveMutation.mutateAsync({
              id: editing?.id,
              name,
              active: values.active !== false,
            });
            showSuccess(editing ? 'Category updated' : 'Category created');
            setModalOpen(false);
            setEditing(null);
            await refetch();
          } catch (e: unknown) {
            showError(e instanceof Error ? e.message : 'Failed to save category');
          }
        }}
        initialValues={modalInitialValues}
        fieldGroups={categoryFieldGroups}
        title={editing ? 'Edit category' : 'Add category'}
      />

      <TitleBar
        breadcrumbs={[
          { label: 'Home', href: '/app/dashboard' },
          { label: 'Catalog', href: '/app/toms-vending/inventory' },
          { label: 'Categories' },
        ]}
        title="Categories"
        rightContent={
          <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none' }}>
            + Add category
          </Button>
        }
      />

      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            placeholder="Search categories…"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 300 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Show inactive"
          />
        </Box>

        <MaterialReactTable
          columns={columns}
          data={filteredData}
          enableGlobalFilter={false}
          manualPagination={false}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={filteredData.length}
          state={{ isLoading, pagination, sorting }}
          initialState={{ density: 'compact', sorting: [{ id: 'name', desc: false }] }}
        />
      </Box>
    </>
  );
};

export default TomsVendingCategoriesPage;
